// Vehicle.tsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Form,
  Button,
  Card,
  Col,
  Row,
  ProgressBar,
  Badge,
} from "react-bootstrap";
import { useTranslate } from "../hooks/LanguageProvider";
import SelectGroup from "../components/Supplier/SelectGroupVehicle";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from "react-router-dom";
import { Bounce, toast } from "react-toastify";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface VehicleInterface {
  id_vehicule: number;
  id_conducteur: number | null;
  category_vehicule: string;
  couleur_vehicule: string;
  id_groupe: number | null;
  id_user: number | null;

  immatriculation_vehicule: string;
  num_porte_vehicule: string;

  // Classification (intégration demandée)
  num_chassis_vehicule: string; // VIN
  modele_vehicule: string; // Marque (mapping temporaire)
  propriete_vehicule?: string; // Référence (colonne existante dans la table)

  vehicule_type: string;

  // existants
  GPSDIST: number;
  PSN: string;

  // date (si backend accepte)
  date_aquisition_vehicule?: string | null;
}

interface UserInterface {
  id_user: number;
  nom_user: string;
  prenom_user: string;
}
interface DriverInterface {
  id_conducteur: number;
  nom_conducteur: string;
  prenom_conducteur: string;
}
interface GroupInterface {
  id_groupe: number;
  nom_groupe: string;
}

type OptionType = { value: any; label: string };

const Vehicle: React.FC = () => {
  const { id_vehicule } = useParams<{ id_vehicule?: string }>();
  const { translate } = useTranslate();
  const [acquisitionDateTime, setAcquisitionDateTime] = useState<Date | null>(
    new Date(),
  );
  const user_id = localStorage.getItem("userid");

  const [Vehicle, setVehicle] = useState<VehicleInterface>({
    id_vehicule: 0,
    id_conducteur: null,
    category_vehicule: "PL",
    couleur_vehicule: "#000000",
    id_groupe: null,
    id_user: null,

    immatriculation_vehicule: "",
    num_porte_vehicule: "",

    num_chassis_vehicule: "",
    modele_vehicule: "",
    propriete_vehicule: "",

    vehicule_type: "Tracteur",

    GPSDIST: 0,
    PSN: "",
    date_aquisition_vehicule: null,
  });

  const [Users, setUsers] = useState<UserInterface[]>([]);
  const [Drivers, setDrivers] = useState<DriverInterface[]>([]);
  const [groups, setGroups] = useState<GroupInterface[]>([]);

  const [typesOptions, setTypesOptions] = useState<any[]>([
    { value: "Aucun", label: "Aucun" },
  ]);
  const [groupsOptions, setGroupsOptions] = useState<any[]>([
    { value: "Aucun", label: "Aucun" },
  ]);
  const [usersOptions, setUsersOptions] = useState<any[]>([
    { value: "Aucun", label: "Aucun" },
  ]);
  const [driversOptions, setDriversOptions] = useState<any[]>([
    { value: "Aucun", label: "Aucun" },
  ]);

  const [buttonClicked, setButtonClicked] = useState(false);
  const [updateMatriculation, setUpdateMatriculation] = useState("");

  const navigate = useNavigate();

  // ===============================
  // Data loaders (inchangés)
  // ===============================
  useEffect(() => {
    if (user_id) {
      getUser(user_id);
      getGroup(user_id);
      getDriver(user_id);
    }
  }, []);

  useEffect(() => {
    if (id_vehicule) getVehicle();
  }, []);

  const getGroup = async (user_id: any) => {
    try {
      const res = await fetch(
        `${backendUrl}/api/groups-vehicule/groups/${user_id}`,
        { mode: "cors" },
      );
      if (!res.ok)
        return console.error("Erreur lors de la récupération des groupes");

      const groupsData = await res.json();
      setGroups(groupsData);

      setGroupsOptions(
        groupsData.map((group: any) => ({
          value: group.id_groupe,
          label: `${group.nom_groupe || ""}`,
        })),
      );
    } catch (error) {
      console.error("Erreur lors de la récupération des groupes", error);
    }
  };

  const getUser = async (user_id: any) => {
    try {
      const res = await fetch(`${backendUrl}/api/users/find/${user_id}`, {
        mode: "cors",
      });
      if (!res.ok)
        return console.error("Erreur lors de la récupération des utilisateurs");

      const usersData = await res.json();
      setUsers(usersData);

      setUsersOptions(
        usersData.map((user: any) => ({
          value: user.id_user,
          label: `${user.nom_user} ${user.prenom_user || ""}`,
        })),
      );
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs", error);
    }
  };

  const getDriver = async (user_id: any) => {
    try {
      const res = await fetch(`${backendUrl}/api/drivers/find/${user_id}`, {
        mode: "cors",
      });
      if (!res.ok)
        return console.error("Erreur lors de la récupération des conducteurs");

      const driverData = await res.json();
      setDrivers(driverData);

      setDriversOptions(
        driverData.map((driver: any) => ({
          value: driver.id_conducteur,
          label: `${driver.nom_conducteur} ${driver.prenom_conducteur || ""}`,
        })),
      );
    } catch (error) {
      console.error("Erreur lors de la récupération des conducteurs", error);
    }
  };

  const getVehicle = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/vehicle/find/${id_vehicule}`, {
        mode: "cors",
      });
      if (!res.ok) return console.error("Error while retrieving the vehicle");

      const data = await res.json();

      setVehicle((prev) => ({
        ...prev,
        id_vehicule: data.id_vehicule,
        id_conducteur:
          data.id_conducteur ?? data.id_conducteur_vehicule ?? null,
        category_vehicule: data.category_vehicule,
        couleur_vehicule: data.couleur_vehicule,
        id_groupe: data.id_groupe,
        id_user: data.id_user,
        immatriculation_vehicule: data.immatriculation_vehicule,
        num_porte_vehicule: data.num_porte_vehicule,
        vehicule_type: data.vehicule_type,
        GPSDIST: data.GPSDIST ?? 0,
        PSN: data.PSN ?? "",

        // ✅ si l’API renvoie un jour ces champs, on les prend directement
        num_chassis_vehicule:
          data.num_chassis_vehicule ?? prev.num_chassis_vehicule,
        modele_vehicule: data.modele_vehicule ?? prev.modele_vehicule,
        propriete_vehicule: data.propriete_vehicule ?? prev.propriete_vehicule,
        date_aquisition_vehicule:
          data.date_aquisition_vehicule ?? prev.date_aquisition_vehicule,
      }));

      setUpdateMatriculation(data.immatriculation_vehicule);

      try {
        const res = await fetch(
          `${backendUrl}/api/vehicle/type-options/${data.category_vehicule}`,
          { mode: "cors" },
        );
        if (!res.ok)
          return console.error("Erreur lors de la récupération des types");

        const typesData = await res.json();
        const typesOptionsData = typesData.model
          .split(",")
          .map((type: any) => ({
            value: type,
            label: `${type || ""}`,
          }));
        setTypesOptions(typesOptionsData);
      } catch (error) {
        console.error("Erreur lors de la récupération des types", error);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du véhicule", error);
    }
  };

  // ===============================
  // Handlers
  // ===============================
  const handleVehicleChange = (updatedValues: Partial<VehicleInterface>) => {
    setVehicle((prev) => ({ ...prev, ...updatedValues }));
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setVehicle((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleUserChange = (selectedOption: OptionType | null) => {
    setVehicle((prev) => {
      const next = {
        ...prev,
        id_user: selectedOption ? selectedOption.value : null,
      };
      if (selectedOption) {
        getGroup(selectedOption.value);
        getDriver(selectedOption.value);
      }
      return next;
    });
  };

  const handleDriverChange = (selectedOption: OptionType | null) => {
    setVehicle((prev) => ({
      ...prev,
      id_conducteur: selectedOption ? selectedOption.value : null,
    }));
  };

  const handleGroupChange = (selectedOption: OptionType | null) => {
    setVehicle((prev) => ({
      ...prev,
      id_groupe: selectedOption ? selectedOption.value : null,
    }));
  };

  const handleCategoryChange = async (selectedOption: OptionType | null) => {
    if (!selectedOption) return;

    try {
      const res = await fetch(
        `${backendUrl}/api/vehicle/type-options/${selectedOption.value}`,
        { mode: "cors" },
      );
      if (!res.ok)
        return console.error("Erreur lors de la récupération des types");

      const typesData = await res.json();
      const typesOptionsData = typesData.model.split(",").map((type: any) => ({
        value: type,
        label: type || "",
      }));

      setTypesOptions(typesOptionsData);

      setVehicle((prev) => ({
        ...prev,
        category_vehicule: selectedOption.value,
        vehicule_type: "",
      }));
    } catch (error) {
      console.error("Erreur lors de la récupération des types", error);
    }
  };

  const handleTypeChange = (selectedOption: OptionType | null) => {
    setVehicle((prev) => ({
      ...prev,
      vehicule_type: selectedOption ? selectedOption.value : "",
    }));
  };

  const cancelClicked = () => navigate("/Vehicles");

  // ===============================
  // Validation (on ajoute VIN/Marque/Référence)
  // ===============================
  const validateNotEmpty = (v: string) => (v ?? "").trim() !== "";

  const setBorder = (id: string, ok: boolean) => {
    const el = document.getElementById(id) as HTMLElement | null;
    if (!el) return;
    (el as any).style.borderColor = ok ? "#ced4da" : "red";
    (el as any).style.border = ok ? "" : "1px solid red";
  };

  // ===============================
  // APIs: mêmes endpoints, payload enrichi
  // ===============================
  const updateVehicle = async (vehicle: VehicleInterface) => {
    const okImm = validateNotEmpty(vehicle.immatriculation_vehicule);
    const okDoor = validateNotEmpty(vehicle.num_porte_vehicule);
    const okVin = validateNotEmpty(vehicle.num_chassis_vehicule);
    const okBrand = validateNotEmpty(vehicle.modele_vehicule);
    const okRef = validateNotEmpty(vehicle.propriete_vehicule || "");
    const okUser = vehicle.id_user != null;
    const okGroup = vehicle.id_groupe != null;

    if (
      !okImm ||
      !okDoor ||
      !okVin ||
      !okBrand ||
      !okRef ||
      !okUser ||
      !okGroup
    ) {
      setBorder("immatriculation_vehicule", okImm);
      setBorder("num_porte_vehicule", okDoor);
      setBorder("num_chassis_vehicule", okVin);
      setBorder("modele_vehicule", okBrand);
      setBorder("propriete_vehicule", okRef);
      setBorder("user", okUser);
      setBorder("group", okGroup);

      toast.warn(translate("Please fill in all required fields"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
      setButtonClicked(false);
      return;
    }

    const vehicleData = {
      // anciens champs (inchangés)
      category_vehicule: vehicle.category_vehicule,
      couleur_vehicule: vehicle.couleur_vehicule,
      id_conducteur_vehicule: vehicle.id_conducteur,
      id_groupe: vehicle.id_groupe,
      id_user: vehicle.id_user,
      immatriculation_vehicule: vehicle.immatriculation_vehicule,
      num_porte_vehicule: vehicle.num_porte_vehicule,
      vehicule_type: vehicle.vehicule_type,
      id_vehicule: vehicle.id_vehicule,
      GPSDIST: vehicle.GPSDIST,
      PSN: vehicle.PSN,

      // ✅ intégration demandée
      num_chassis_vehicule: vehicle.num_chassis_vehicule,
      modele_vehicule: vehicle.modele_vehicule,
      propriete_vehicule: vehicle.propriete_vehicule,
      date_aquisition_vehicule: acquisitionDateTime
        ? acquisitionDateTime.toISOString().slice(0, 19).replace("T", " ")
        : null,
    };

    try {
      const recheck = await fetch(`${backendUrl}/api/vehicle/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        body: JSON.stringify({
          immatriculation_vehicule: vehicle.immatriculation_vehicule,
          updatedImmatriculation: updateMatriculation,
          updated:
            updateMatriculation === vehicle.immatriculation_vehicule ? 0 : 1,
        }),
      });

      if (!recheck.ok) throw new Error("recheck failed");

      const jsonResponse = await recheck.json();
      if (jsonResponse.vehicle_count != 0) {
        toast.warn(translate("Vehicle immatriculation already exists ?"), {
          position: "bottom-right",
          autoClose: 2400,
          transition: Bounce,
        });
        setButtonClicked(false);
        return;
      }

      const res = await fetch(`${backendUrl}/api/vehicle/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        body: JSON.stringify(vehicleData),
      });

      if (!res.ok) {
        toast.warn(translate("Can't updating vehicle"), {
          position: "bottom-right",
          autoClose: 2400,
          transition: Bounce,
        });
        setButtonClicked(false);
        return;
      }

      toast.success(translate("Vehicle updated successfully"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });

      setButtonClicked(false);
      navigate("/Vehicles");
    } catch (error) {
      toast.warn(translate("Can't updating vehicle"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
      setButtonClicked(false);
    }
  };

  const createVehicle = async (vehicle: VehicleInterface) => {
    const okImm = validateNotEmpty(vehicle.immatriculation_vehicule);
    const okDoor = validateNotEmpty(vehicle.num_porte_vehicule);
    const okVin = validateNotEmpty(vehicle.num_chassis_vehicule);
    const okBrand = validateNotEmpty(vehicle.modele_vehicule);
    const okRef = validateNotEmpty(vehicle.propriete_vehicule || "");
    const okUser = vehicle.id_user != null;
    const okGroup = vehicle.id_groupe != null;

    if (
      !okImm ||
      !okDoor ||
      !okVin ||
      !okBrand ||
      !okRef ||
      !okUser ||
      !okGroup
    ) {
      setBorder("immatriculation_vehicule", okImm);
      setBorder("num_porte_vehicule", okDoor);
      setBorder("num_chassis_vehicule", okVin);
      setBorder("modele_vehicule", okBrand);
      setBorder("propriete_vehicule", okRef);
      setBorder("user", okUser);
      setBorder("group", okGroup);

      toast.warn(translate("Please fill in all required fields"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
      setButtonClicked(false);
      return;
    }

    try {
      const vehicleData = {
        id_vehicule: vehicle.id_vehicule,
        category_vehicule: vehicle.category_vehicule,
        couleur_vehicule: vehicle.couleur_vehicule,
        id_conducteur_vehicule: vehicle.id_conducteur,
        id_groupe: vehicle.id_groupe,
        id_user: vehicle.id_user,
        immatriculation_vehicule: vehicle.immatriculation_vehicule,
        num_porte_vehicule: vehicle.num_porte_vehicule,
        vehicule_type: vehicle.vehicule_type,
        GPSDIST: vehicle.GPSDIST,
        PSN: vehicle.PSN,

        // ✅ intégration demandée
        num_chassis_vehicule: vehicle.num_chassis_vehicule,
        modele_vehicule: vehicle.modele_vehicule,
        propriete_vehicule: vehicle.propriete_vehicule,
        date_aquisition_vehicule: acquisitionDateTime
          ? acquisitionDateTime.toISOString().slice(0, 19).replace("T", " ")
          : null,
      };

      const recheck = await fetch(`${backendUrl}/api/vehicle/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        body: JSON.stringify({
          immatriculation_vehicule: vehicle.immatriculation_vehicule,
          updatedImmatriculation: updateMatriculation,
          update: 0,
        }),
      });

      if (!recheck.ok) throw new Error("recheck failed");

      const jsonResponse = await recheck.json();
      if (jsonResponse.vehicle_count != 0) {
        toast.warn(translate("Vehicle immatriculation already exists ?"), {
          position: "bottom-right",
          autoClose: 2400,
          transition: Bounce,
        });
        setButtonClicked(false);
        return;
      }

      const res = await fetch(`${backendUrl}/api/vehicle/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        body: JSON.stringify(vehicleData),
      });

      if (!res.ok) {
        toast.warn(translate("Can't create a vehicle"), {
          position: "bottom-right",
          autoClose: 2400,
          transition: Bounce,
        });
        setButtonClicked(false);
        return;
      }

      toast.success(translate("Vehicle created successfully !"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });

      navigate("/Vehicles");
      setButtonClicked(false);
    } catch (error) {
      toast.warn(translate("Can't create a vehicle"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
      setButtonClicked(false);
    }
  };

  // ===============================
  // SelectGroup helpers
  // ===============================
  const categoryOptions = [
    { value: "Aucun", label: "Aucun" },
    { value: "PL", label: "PL" },
    { value: "VL", label: "VL" },
    { value: "Engin", label: "Engin" },
    { value: "Outils", label: "Outils" },
    { value: "Utilitaires", label: "Utilitaires" },
    { value: "Autre", label: "Autre" },
  ];

  const findUserById = (id: number) => {
    const u = Users.find((x) => x.id_user === id);
    return u
      ? { value: u.id_user, label: `${u.nom_user} ${u.prenom_user || ""}` }
      : undefined;
  };
  const findDriverById = (id: number) => {
    const d = Drivers.find((x) => x.id_conducteur === id);
    return d
      ? {
          value: d.id_conducteur,
          label: `${d.nom_conducteur} ${d.prenom_conducteur || ""}`,
        }
      : undefined;
  };
  const findGroupById = (id: number) => {
    const g = groups.find((x) => x.id_groupe === id);
    return g
      ? { value: g.id_groupe, label: `${g.nom_groupe || ""}` }
      : undefined;
  };

  // ===============================
  // Sidebar recap + completion %
  // ===============================
  const safeText = (v: any) => {
    const s = String(v ?? "").trim();
    return s === "" ? "—" : s;
  };

  const completion = useMemo(() => {
    const isFilled = (v: any) => {
      if (v === null || v === undefined) return false;
      if (typeof v === "number") return !Number.isNaN(v); // 0 = OK
      const s = String(v).trim();
      if (s === "" || s === "—") return false;
      if (s.toLowerCase() === "aucun") return false;
      return true;
    };

    // ✅ Règles EXACTES (et on stocke aussi la valeur lue)
    const rules = [
      {
        key: "immatriculation",
        label: "Immatriculation",
        ok: isFilled(Vehicle.immatriculation_vehicule),
        value: Vehicle.immatriculation_vehicule,
      },
      {
        key: "porte",
        label: "N° porte",
        ok: isFilled(Vehicle.num_porte_vehicule),
        value: Vehicle.num_porte_vehicule,
      },
      {
        key: "vin",
        label: "VIN",
        ok: isFilled(Vehicle.num_chassis_vehicule),
        value: Vehicle.num_chassis_vehicule,
      },

      {
        key: "categorie",
        label: "Catégorie",
        ok: isFilled(Vehicle.category_vehicule),
        value: Vehicle.category_vehicule,
      },
      {
        key: "type",
        label: "Type",
        ok: isFilled(Vehicle.vehicule_type),
        value: Vehicle.vehicule_type,
      },
      {
        key: "marque",
        label: "Marque",
        ok: isFilled(Vehicle.modele_vehicule),
        value: Vehicle.modele_vehicule,
      },
      {
        key: "reference",
        label: "Référence",
        ok: isFilled(Vehicle.propriete_vehicule),
        value: Vehicle.propriete_vehicule,
      },

      {
        key: "user",
        label: "Utilisateur",
        ok: isFilled(Vehicle.id_user),
        value: Vehicle.id_user,
      },
      {
        key: "group",
        label: "Groupe",
        ok: isFilled(Vehicle.id_groupe),
        value: Vehicle.id_groupe,
      },

      {
        key: "device",
        label: "Dispositif (PSN)",
        ok: isFilled(Vehicle.PSN),
        value: Vehicle.PSN,
      },

      // Couleur: tu peux la retirer si tu ne veux pas la compter
      {
        key: "color",
        label: "Couleur",
        ok: isFilled(Vehicle.couleur_vehicule),
        value: Vehicle.couleur_vehicule,
      },
    ];

    const total = rules.length;
    const done = rules.filter((r) => r.ok).length;
    const percent = total ? Math.round((done / total) * 100) : 0;

    const missing = rules.filter((r) => !r.ok).map((r) => r.label);

    const status =
      percent === 100 ? "complete" : percent >= 70 ? "almost" : "incomplete";

    return { total, done, percent, status, missing, rules };
  }, [Vehicle]);

  const progressVariant =
    completion.status === "complete"
      ? "success"
      : completion.status === "almost"
        ? "primary"
        : "warning";

  const statusChip = () => {
    if (completion.status === "complete") {
      return (
        <span className="veh-chip veh-chip-ok">
          <i className="bi bi-check-circle" /> {translate("Complet")}
        </span>
      );
    }
    if (completion.status === "almost") {
      return (
        <span className="veh-chip veh-chip-info">
          <i className="bi bi-info-circle" /> {translate("Presque")}
        </span>
      );
    }
    return (
      <span className="veh-chip veh-chip-warn">
        <i className="bi bi-exclamation-circle" /> {translate("Incomplet")}
      </span>
    );
  };

  // ===============================
  // UI
  // ===============================
  return (
    <>
      <style>{`
      
        .veh-card { border: 1px solid rgba(0,0,0,.06); border-radius: 18px; box-shadow: 0 12px 30px rgba(18,38,63,.08); overflow:hidden; }
        .veh-section { padding: 18px 20px; border-bottom: 1px solid rgba(0,0,0,.06); }
        .veh-section:last-child { border-bottom: 0; }
        .veh-section-title { font-weight: 800; font-size: 16px; color: #1f2a37; margin-bottom: 5px; display:flex; align-items:center; gap:8px; }
        .veh-actions { position: sticky; bottom: 0; z-index: 10; background: rgba(255,255,255,.92); backdrop-filter: blur(10px); border-top: 1px solid rgba(0,0,0,.06); padding: 12px 16px; }
        .veh-sidebar { position: sticky; top: 18px; }
        .veh-sidebar-card { border: 1px solid rgba(0,0,0,.06); border-radius: 18px; box-shadow:0 10px 22px rgba(18,38,63,.06); overflow:hidden; }
        .veh-sidebar-header { padding: 16px 16px 12px; border-bottom:1px solid rgba(0,0,0,.06); background: linear-gradient(180deg, #fff, rgba(255,255,255,.92)); }
        .veh-kv { padding: 12px 16px; }
        .veh-kv-row { display:flex; justify-content:space-between; gap:10px; padding: 10px 0; border-bottom: 1px dashed rgba(0,0,0,.12); }
        .veh-kv-row:last-child { border-bottom: 0; }
        .veh-k { color:#64748b; font-size: 12px; }
        .veh-v { font-weight: 800; color:#111827; font-size: 13px; text-align:right; }
        .veh-chip { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:999px; font-weight:800; font-size:12px; border:1px solid rgba(0,0,0,.10); background:#fff; white-space:nowrap; }
        .veh-chip-ok { border-color: rgba(25,135,84,.18); background: rgba(25,135,84,.08); color:#198754; }
        .veh-chip-warn { border-color: rgba(255,193,7,.25); background: rgba(255,193,7,.12); color:#8a6d00; }
        .veh-chip-info { border-color: rgba(13,110,253,.20); background: rgba(13,110,253,.10); color:#0d6efd; }
        .veh-dot { width:12px; height:12px; border-radius:999px; border:1px solid rgba(0,0,0,.15); display:inline-block; vertical-align:middle; }
      `}</style>

      <div className="veh-page">
        {/* Header */}
        <div className="row mb-3">
          <div className="col-12 d-flex align-items-start justify-content-between">
            <div>
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-sliders text-primary fs-4" />
                <h3 className="m-0 fw-bold">
                  {id_vehicule
                    ? translate("Edit vehicle")
                    : translate("New vehicle")}
                </h3>
                <Badge bg="light" text="dark" className="border">
                  {translate("Tool")}
                </Badge>
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {translate("Configuration only (no tracking metrics)")}
              </div>
            </div>

            <div className="d-none d-md-flex gap-2">
              <Button variant="outline-secondary" onClick={cancelClicked}>
                <i className="bi bi-arrow-left" /> {translate("Back")}
              </Button>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {/* LEFT: Form */}
          <div className="col-lg-9">
            <Card className="veh-card">
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                }}
              >
                {/* INFORMATIONS */}
                <div className="veh-section">
                  <div className="veh-section-title">
                    <i className="las la-car text-primary" />{" "}
                    {translate("Informations")}
                  </div>

                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group
                        controlId="immatriculation_vehicule"
                        style={{ fontSize: "14px" }}
                      >
                        <Form.Label>
                          <strong>
                            {" "}
                            {translate("Matriculation")} {translate("Vehicle")}
                          </strong>{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Saisir l'immatriculation du véhicule"
                          name="immatriculation_vehicule"
                          value={Vehicle.immatriculation_vehicule}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group
                        controlId="num_porte_vehicule"
                        style={{ fontSize: "14px" }}
                      >
                        <Form.Label>
                          <strong> {translate("Door number")} </strong>{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Saisir le numéro porte"
                          name="num_porte_vehicule"
                          value={Vehicle.num_porte_vehicule}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group
                        controlId="acquisitionDateTime"
                        style={{ fontSize: "14px" }}
                      >
                        <Form.Label>
                          {" "}
                          <strong>{translate("Acquisition date")} </strong>
                        </Form.Label>
                        <div className="input-group">
                          <DatePicker
                            selected={acquisitionDateTime}
                            onChange={(date: Date | null) =>
                              setAcquisitionDateTime(date)
                            }
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat="MM/dd/yyyy h:mm aa"
                            customInput={
                              <Form.Control
                                type="text"
                                placeholder="Select Date and Time"
                              />
                            }
                          />
                          <span className="input-group-text">
                            <i className="las la-calendar" />
                          </span>
                        </div>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group
                        controlId="num_chassis_vehicule"
                        style={{ fontSize: "14px" }}
                      >
                        <Form.Label>
                          <strong> {translate("N° châssis (VIN)")} </strong>
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ex: VF1XXXXXXXXXXXXXX"
                          name="num_chassis_vehicule"
                          value={Vehicle.num_chassis_vehicule}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group
                        controlId="GPSDIST"
                        style={{ fontSize: "14px" }}
                      >
                        <Form.Label>
                          <strong>{translate("Odometer")}</strong>
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          placeholder={translate("Enter the Odometer")}
                          name="GPSDIST"
                          value={Vehicle.GPSDIST}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* CLASSIFICATION */}
                <div className="veh-section">
                  <div className="veh-section-title">
                    <i className="las la-tags text-primary" />{" "}
                    {translate("Classification")}
                  </div>
                  <Row className="g-3">
                    <Col md={6}>
                      <SelectGroup
                        controlId="category_vehicule"
                        name={"category_vehicule"}
                        label={
                          translate("Category") + " " + translate("Vehicle")
                        }
                        icon="search"
                        options={categoryOptions}
                        valueType={{
                          value: Vehicle.category_vehicule || "",
                          label: Vehicle.category_vehicule,
                        }}
                        onChange={handleCategoryChange}
                      />
                    </Col>

                    <Col md={6}>
                      <SelectGroup
                        controlId="type_vehicule"
                        name={"type_vehicule"}
                        label={translate("Vehicle type")}
                        icon="car"
                        options={typesOptions}
                        valueType={{
                          value: Vehicle.vehicule_type || "",
                          label: Vehicle.vehicule_type || "",
                        }}
                        onChange={handleTypeChange}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="modele_vehicule">
                        <Form.Label>
                          {translate("Marque")}{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ex: Renault /Toyota /MAN ..."
                          name="modele_vehicule"
                          value={Vehicle.modele_vehicule}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="propriete_vehicule">
                        <Form.Label>
                          {translate("Référence")}{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Référence interne / externe"
                          name="propriete_vehicule"
                          value={Vehicle.propriete_vehicule || ""}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* AFFECTATION */}
                <div className="veh-section">
                  <div className="veh-section-title">
                    <i className="las la-user text-primary" />{" "}
                    {translate("Affectation")}
                  </div>

                  <Row className="g-3">
                    <Col md={6}>
                      <SelectGroup
                        controlId="user"
                        name={"user"}
                        label={translate("User")}
                        icon="user"
                        options={usersOptions}
                        valueType={{
                          value: Vehicle.id_user,
                          label:
                            findUserById(Vehicle.id_user || 0)?.label || "",
                        }}
                        onChange={handleUserChange}
                      />
                    </Col>

                    <Col md={6}>
                      <SelectGroup
                        controlId="driver"
                        name={"Driver"}
                        label={translate("Driver")}
                        icon="user"
                        options={driversOptions}
                        valueType={{
                          value: Vehicle.id_conducteur,
                          label:
                            findDriverById(Vehicle.id_conducteur || 0)?.label ||
                            "",
                        }}
                        onChange={handleDriverChange}
                      />
                    </Col>

                    <Col md={6}>
                      <SelectGroup
                        controlId="group"
                        name={"group"}
                        label={translate("Group")}
                        icon="users"
                        options={groupsOptions}
                        valueType={{
                          value: Vehicle.id_groupe,
                          label:
                            findGroupById(Vehicle.id_groupe || 0)?.label || "",
                        }}
                        onChange={handleGroupChange}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="PSN">
                        <Form.Label>
                          <strong>{translate("Dispositif")} (PSN) </strong>{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder={translate("Select/enter device")}
                          name="PSN"
                          value={Vehicle.PSN}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* APPARENCE */}
                <div className="veh-section">
                  <div className="veh-section-title">
                    <i className="las la-palette text-primary" />{" "}
                    {translate("Appearance")}
                  </div>

                  <Row className="g-3 align-items-end">
                    <Col md={6}>
                      <Form.Group
                        controlId="formGroupColor"
                        style={{ fontSize: "14px" }}
                      >
                        <Form.Label>
                          <strong>{translate("Color")}</strong>{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Control
                            type="color"
                            value={Vehicle.couleur_vehicule}
                            onChange={(e) =>
                              handleVehicleChange({
                                couleur_vehicule: e.target.value,
                              })
                            }
                            required
                            style={{ width: 70 }}
                          />
                          <Form.Control
                            type="text"
                            value={Vehicle.couleur_vehicule}
                            onChange={(e) =>
                              handleVehicleChange({
                                couleur_vehicule: e.target.value,
                              })
                            }
                            pattern="#[0-9a-fA-F]{6}"
                            title="Veuillez entrer un code couleur hexadécimal valide (par exemple, #00ff00)"
                            required
                            style={{ maxWidth: 160 }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Footer actions */}
                <div className="veh-actions d-flex align-items-center justify-content-end gap-2">
                  <Button
                    onClick={() => {
                      setButtonClicked(true);
                      id_vehicule
                        ? updateVehicle(Vehicle)
                        : createVehicle(Vehicle);
                    }}
                    variant="primary"
                    type="submit"
                    disabled={buttonClicked}
                  >
                    {id_vehicule
                      ? translate("Save changes")
                      : translate("Create vehicle")}
                  </Button>

                  <Button
                    onClick={cancelClicked}
                    type="button"
                    variant="outline-secondary"
                    disabled={buttonClicked}
                  >
                    {translate("Cancel")}
                  </Button>
                </div>
              </Form>
            </Card>
          </div>

          {/* RIGHT: Sidebar recap */}
          <div className="col-lg-3">
            <div className="veh-sidebar">
              <Card className="veh-sidebar-card">
                <div className="veh-sidebar-header">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="fw-bold">
                      {translate("Récap configuration")}
                    </div>
                    {statusChip()}
                  </div>

                  <div
                    className="text-muted"
                    style={{ fontSize: 12, marginTop: 4 }}
                  >
                    {translate("État représentatif de la configuration")}
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <div
                      className="d-flex justify-content-between"
                      style={{ marginBottom: 6 }}
                    >
                      <small className="text-muted">
                        {translate("Complétion")}
                      </small>
                      <small className="fw-bold">{completion.percent}%</small>
                    </div>
                    <ProgressBar
                      now={completion.percent}
                      variant={progressVariant as any}
                      style={{ height: 8, borderRadius: 999 }}
                    />
                    {completion.missing.length > 0 && (
                      <div className="mt-3">
                        <div
                          className="text-muted"
                          style={{ fontSize: 12, marginBottom: 6 }}
                        >
                          Champs manquants :
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {completion.missing.map((m: string) => (
                            <span
                              key={m}
                              className="badge text-bg-light border"
                            >
                              {m}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="veh-kv">
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Immatriculation")}</div>
                    <div className="veh-v">
                      {safeText(Vehicle.immatriculation_vehicule)}
                    </div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("N° porte")}</div>
                    <div className="veh-v">
                      {safeText(Vehicle.num_porte_vehicule)}
                    </div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("VIN")}</div>
                    <div className="veh-v">
                      {safeText(Vehicle.num_chassis_vehicule)}
                    </div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Catégorie")}</div>
                    <div className="veh-v">
                      {safeText(Vehicle.category_vehicule)}
                    </div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Type")}</div>
                    <div className="veh-v">
                      {safeText(Vehicle.vehicule_type)}
                    </div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Marque")}</div>
                    <div className="veh-v">
                      {safeText(Vehicle.modele_vehicule)}
                    </div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Référence")}</div>
                    <div className="veh-v">
                      {safeText(Vehicle.propriete_vehicule)}
                    </div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Utilisateur")}</div>
                    <div className="veh-v">
                      {safeText(findUserById(Vehicle.id_user || 0)?.label)}
                    </div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Groupe")}</div>
                    <div className="veh-v">
                      {safeText(findGroupById(Vehicle.id_groupe || 0)?.label)}
                    </div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Dispositif")}</div>
                    <div className="veh-v">{safeText(Vehicle.PSN)}</div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Couleur")}</div>
                    <div className="veh-v">
                      <span
                        className="veh-dot"
                        style={{
                          background: Vehicle.couleur_vehicule || "#d1d5db",
                        }}
                      />
                      <span style={{ marginLeft: 8 }}>
                        {safeText(Vehicle.couleur_vehicule)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="px-3 pb-3">
                  <div
                    className="text-muted"
                    style={{ fontSize: 12, marginBottom: 10 }}
                  >
                    <i className="bi bi-info-circle" />{" "}
                    {translate("Validation configuration uniquement")}
                  </div>

                  <div className="d-grid gap-2">
                    <Button
                      variant="outline-primary"
                      type="button"
                      onClick={() =>
                        toast.info(translate("Export (à brancher)"))
                      }
                    >
                      <i className="bi bi-download" />{" "}
                      {translate("Exporter fiche")}
                    </Button>

                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => window.print()}
                    >
                      <i className="bi bi-printer" /> {translate("Imprimer")}
                    </Button>

                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            Vehicle.propriete_vehicule || "",
                          );
                          toast.success(translate("Copied"), {
                            position: "bottom-right",
                            autoClose: 1400,
                            transition: Bounce,
                          });
                        } catch {
                          toast.warn(translate("Copy failed"), {
                            position: "bottom-right",
                            autoClose: 1400,
                            transition: Bounce,
                          });
                        }
                      }}
                    >
                      <i className="bi bi-clipboard" />{" "}
                      {translate("Copier référence")}
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Vehicle;
